import { ParaglideMessage } from '@inlang/paraglide-js-react';
import { m } from '@/paraglide/messages';

/**
 * Link from an endpoint page to its generated API reference.
 *
 * The two pages are deliberately split by axis: this page owns price, lane
 * routing and measured uptime; the reference owns parameters and response
 * schema. Restating either on the other would turn a slug-matched pair into
 * genuine duplicates, so the pair is joined by a link instead of by copied
 * content.
 *
 * The URL is a mirror of this page's own path, not a guess. The gateway pins it
 * with `x-mint.href` on every operation (server/internal/httpapi/openapi_catalog.go),
 * precisely so neither side has to reproduce Mintlify's summary slugifier - which
 * turns "X / Twitter Article" into "x-twitter-article" and keeps a literal "+" in
 * "linkedin-profile-search-+-email", and which three candidate rules each failed to
 * match on roughly 4% of the catalog.
 *
 * The reference is noindex by design (src/data/docsPages.ts), so this link is for
 * readers, not for search. The link that carries crawl equity runs the other way,
 * from the reference page back to here.
 */
export const docsReferenceHref = (apiId: string, endpointId: string): string =>
  `/docs/api-reference/${apiId}/${endpointId}`;

export const DocsReferenceLink = ({
  apiId,
  endpointId,
}: {
  apiId: string;
  endpointId: string;
}) => (
  <p className="mt-3 text-13 text-text-3">
    <ParaglideMessage
      message={m.endpoint_docs_link}
      markup={{
        link: ({ children }) => (
          <a className="text-accent hover:underline" href={docsReferenceHref(apiId, endpointId)}>
            {children}
          </a>
        ),
      }}
    />
  </p>
);
